import {Card} from 'shells/card/card';
import {DistributionBar} from 'shells/card/distribution-bar/distribution-bar';
import type {ProportionalProps} from 'shells/card/distribution-bar/types';
import {colors} from 'shells/colors/colors';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import type {GetChatbotReport_chatbotReport_aiMetrics_sentimentBreakdown as SentimentBreakdown} from 'nutshell-graphql-types';

import {
    BAD_SECTION_DEFAULTS,
    EMPTY_STATE_SECTION_DEFAULTS,
    GOOD_SECTION_DEFAULTS,
    LEGEND_ITEMS,
    THREAD_SIDEBAR_OPEN_SOURCES,
    NEUTRAL_SECTION_DEFAULTS,
} from './consts';
import {LegendItem} from './legend-item';
import {useOpenThreadListSidebar} from './use-open-thread-list-sidebar';

interface Props {
    sentimentBreakdown: SentimentBreakdown | null;
}

export function ChatbotFeedback(props: Props) {
    const openThreadListSidebar = useOpenThreadListSidebar();

    const openSidebar = (
        threadIds: string[],
        title: string,
        subtitle: string
    ): (() => void) | undefined => {
        if (threadIds.length === 0) {
            return;
        }

        return () => {
            openThreadListSidebar({
                threadIds,
                title,
                subtitle,
                source: THREAD_SIDEBAR_OPEN_SOURCES.CHATBOT_FEEDBACK_SECTION,
            });
        };
    };

    const goodThreadIds = props.sentimentBreakdown?.goodSampleThreadIds ?? [];
    const badThreadIds = props.sentimentBreakdown?.badSampleThreadIds ?? [];
    const neutralThreadIds = props.sentimentBreakdown?.neutralSampleThreadIds ?? [];

    const barProps: ProportionalProps = {
        size: 'mini',
        proportional: true,
        showAsPercentage: true,
        sections: props.sentimentBreakdown
            ? [
                  {
                      ...GOOD_SECTION_DEFAULTS,
                      value: props.sentimentBreakdown.goodPercent ?? 0,
                      onClick: openSidebar(goodThreadIds, 'Good threads', 'Threads rated as good'),
                  },
                  {
                      ...NEUTRAL_SECTION_DEFAULTS,
                      value: props.sentimentBreakdown.neutralPercent ?? 0,
                      onClick: openSidebar(
                          neutralThreadIds,
                          'Neutral threads',
                          'Threads rated as neutral'
                      ),
                  },
                  {
                      ...BAD_SECTION_DEFAULTS,
                      value: props.sentimentBreakdown.badPercent ?? 0,
                      onClick: openSidebar(badThreadIds, 'Bad threads', 'Threads rated as bad'),
                  },
              ]
            : [EMPTY_STATE_SECTION_DEFAULTS],
    };

    const renderContent = () => {
        if (!props.sentimentBreakdown) {
            return (
                <div className='flex justify-center'>
                    <Body size='small' color='placeholder'>
                        No threads have been rated yet
                    </Body>
                </div>
            );
        }

        return (
            <Stack isHorizontal={true} spacing={8}>
                {LEGEND_ITEMS.map((item) => (
                    <LegendItem key={item.label} color={item.color} label={item.label} />
                ))}
            </Stack>
        );
    };

    return (
        <div className='full-width'>
            <Card
                hasBorder={true}
                borderRadius={8}
                hasShadow={true}
                borderColor={colors.buttonSecondaryDk}
            >
                <div className='flex flex-dir-col full-height'>
                    <div className='flex flex-dir-col gap-2 border-bottom px-16 py-12'>
                        <Body isBold={true} color='dark'>
                            Chatbot feedback
                        </Body>
                        <Body color='light' size='small'>
                            Ratio of conversations Nutshell AI rated as good, bad, or neutral
                        </Body>
                    </div>
                    <div className='flex flex-dir-col flex-grow px-16 justify-center'>
                        <Stack spacing={48}>
                            <DistributionBar {...barProps} />
                            {renderContent()}
                        </Stack>
                    </div>
                </div>
            </Card>
        </div>
    );
}
